import React from 'react'
import type { ProductV2Detail, Lang } from './types'

interface ProductSubscriptionV2Props {
  product: ProductV2Detail
  currentLang: Lang
  buyMode: 'single' | 'subscription'
  onModeChange: (m: 'single' | 'subscription') => void
  subscriptionMonths: number
  onMonthsChange: (m: number) => void
  selectedDeliveryDay: number
  onDayChange: (d: number) => void
}

/**
 * ProductSubscriptionV2
 *
 * Optional buy-box module — only renders when product.isSubscription === true.
 * Sits ABOVE the AddToCartButton, lets the buyer toggle one-time vs subscription,
 * pick months and monthly delivery day, and shows total upfront price.
 *
 * Visual style stays Barista-Map: thin black outline, no rounded corners,
 * serif numbers, lowercase labels.
 */
const ProductSubscriptionV2: React.FC<ProductSubscriptionV2Props> = ({
  product,
  currentLang,
  buyMode,
  onModeChange,
  subscriptionMonths,
  onMonthsChange,
  selectedDeliveryDay,
  onDayChange,
}) => {
  const isZh = currentLang === 'zh'
  if (!product.isSubscription) return null

  const formatPrice = (n: number) =>
    Math.floor(n).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')

  const monthOptions = [6, 12, 20]
  const dayOptions = product.deliveryDayOptions && product.deliveryDayOptions.length > 0
    ? product.deliveryDayOptions
    : [1, 5, 10, 15, 20, 25]

  const monthlyPrice = product.subscriptionPrice || product.price
  const totalPrice = monthlyPrice * subscriptionMonths

  return (
    <div className="border border-black/15 bg-white">
      {/* Mode tabs */}
      <div className="grid grid-cols-2 border-b border-black/10">
        <button
          onClick={() => onModeChange('single')}
          className={`py-3 text-[11px] uppercase transition-colors ${
            buyMode === 'single'
              ? 'bg-black text-white'
              : 'bg-white text-black/60 hover:text-black'
          }`}
          style={{ letterSpacing: '0.25em', fontFamily: "'EB Garamond', serif" }}
        >
          {isZh ? '單次購買' : 'One-time'}
        </button>
        <button
          onClick={() => onModeChange('subscription')}
          className={`py-3 text-[11px] uppercase transition-colors ${
            buyMode === 'subscription'
              ? 'bg-black text-white'
              : 'bg-white text-black/60 hover:text-black'
          }`}
          style={{ letterSpacing: '0.25em', fontFamily: "'EB Garamond', serif" }}
        >
          {isZh ? '訂閱方案' : 'Subscribe'}
        </button>
      </div>

      {buyMode === 'subscription' && (
        <div className="space-y-5 p-5">
          <div className="flex items-center justify-between gap-4">
            <span
              className="text-[11px] uppercase text-black/50"
              style={{ letterSpacing: '0.2em' }}
            >
              {isZh ? '訂閱期數' : 'Plan'}
            </span>
            <select
              value={subscriptionMonths}
              onChange={(e) => onMonthsChange(Number(e.target.value))}
              className="h-9 border border-black/15 bg-white px-3 text-sm text-black focus:outline-none"
              style={{ fontFamily: "'EB Garamond', serif" }}
            >
              {monthOptions.map((m) => (
                <option key={m} value={m}>
                  {m} {isZh ? '個月' : 'months'}
                </option>
              ))}
            </select>
          </div>

          <div className="flex items-center justify-between gap-4">
            <span
              className="text-[11px] uppercase text-black/50"
              style={{ letterSpacing: '0.2em' }}
            >
              {isZh ? '每月出貨日' : 'Delivery day'}
            </span>
            <select
              value={selectedDeliveryDay}
              onChange={(e) => onDayChange(Number(e.target.value))}
              className="h-9 border border-black/15 bg-white px-3 text-sm text-black focus:outline-none"
              style={{ fontFamily: "'EB Garamond', serif" }}
            >
              {dayOptions.map((d) => (
                <option key={d} value={d}>
                  {isZh ? `每月 ${d} 日` : `${d}th of month`}
                </option>
              ))}
            </select>
          </div>

          <div className="space-y-1 border-t border-black/10 pt-4">
            <div className="flex items-center justify-between text-[11px] uppercase text-black/50">
              <span style={{ letterSpacing: '0.2em' }}>
                {isZh ? '月單價' : 'Monthly'}
              </span>
              <span style={{ fontFamily: "'EB Garamond', serif" }}>
                NT$ {formatPrice(monthlyPrice)}
              </span>
            </div>
            <div className="flex items-center justify-between pt-1 text-sm text-black">
              <span style={{ letterSpacing: '0.05em' }}>
                {isZh ? `預收（${subscriptionMonths} 個月）` : `Total (${subscriptionMonths} mo)`}
              </span>
              <span
                className="text-base"
                style={{ fontFamily: "'EB Garamond', serif" }}
              >
                NT$ {formatPrice(totalPrice)}
              </span>
            </div>
          </div>

          <p
            className="text-[11px] leading-relaxed text-black/40"
            style={{ letterSpacing: '0.05em' }}
          >
            {isZh
              ? `每月 ${selectedDeliveryDay} 日固定出貨，共 ${subscriptionMonths} 個月，下單時一次預收。`
              : `Ships on the ${selectedDeliveryDay}th each month for ${subscriptionMonths} months, charged upfront.`}
          </p>
        </div>
      )}
    </div>
  )
}

export default ProductSubscriptionV2
